import { Injectable } from '@angular/core';
import { MacHeaders } from '@app/enum/macHeaders';
import { ScanQueryParametersEnum, ScanQueryParams, GenericScanParameters } from '@app/api/models/scan.models';
import { AppConfigService } from '@app/app-config.service';

export const ESTABLISHMENT_ID = 'est';
export const CUSTOMER_ID = 'customer_uid';
export const TIMESTAMP = 'timestamp';
export const MAC_TOKEN = 'mac';

@Injectable({ providedIn: 'root' })
export class QueryParamatersService {
  private readonly SEPARATOR = '|';

  private queryParameters: URLSearchParams;
  private _customerUID: string;
  private _establishmentID: string;
  private _timestamp: string;
  private _macToken: string;
  private _navigationRoute: string;
  private _validMacUser: boolean;

  constructor(private config: AppConfigService) {
    this.queryParameters = new URLSearchParams(`?${window.location.href.split('?')[1]}`);
    this._validMacUser = false;
    this.customerUID = this.queryParameter(CUSTOMER_ID) || localStorage.getItem(CUSTOMER_ID) || '';
    this.establishmentID = this.queryParameter(ESTABLISHMENT_ID) || localStorage.getItem(ESTABLISHMENT_ID) || '';
    this.timestamp = this.queryParameter(TIMESTAMP) || localStorage.getItem(TIMESTAMP) || '';
    this.macToken = this.queryParameter(MAC_TOKEN) || localStorage.getItem(MAC_TOKEN) || '';
    this._navigationRoute = window.location.href;

    // Automatically set validMacUser to true if all MAC parameters are present and valid
    if (this.validMacArgs()) {
      this._validMacUser = true;
    }
  }

  get customerUID(): string {
    return this._customerUID;
  }

  set customerUID(customerUID: string) {
    this._customerUID = customerUID;

    if (customerUID) {
      localStorage.setItem(CUSTOMER_ID, customerUID);
    }
  }

  get establishmentID(): string {
    return this._establishmentID;
  }

  set establishmentID(establishmentID: string) {
    this._establishmentID = establishmentID;

    if (establishmentID) {
      localStorage.setItem(ESTABLISHMENT_ID, establishmentID);
    }
  }

  get timestamp(): string {
    return this._timestamp;
  }

  set timestamp(timestamp: string) {
    this._timestamp = timestamp;

    if (timestamp) {
      localStorage.setItem(TIMESTAMP, timestamp);
    }
  }

  get macToken(): string {
    return this._macToken;
  }

  set macToken(macToken: string) {
    this._macToken = macToken;

    if (macToken) {
      localStorage.setItem(MAC_TOKEN, macToken);
    }
  }

  get navigationRoute(): string {
    return this.navigationRoute;
  }

  set navigationRoute(navigationRoute: string) {
    this._navigationRoute = navigationRoute;
  }
  get validMacUser(): boolean {
    return this._validMacUser;
  }

  set validMacUser(validMacUser: boolean) {
    this._validMacUser = validMacUser;
  }

  queryParameter(parameter: string): string {
    return this.queryParameters.get(parameter);
  }

  validMacArgs(): boolean {
    return !!(this.customerUID && this.establishmentID && this.timestamp && this.macToken);
  }

  macHeaders() {
    return {
      [MacHeaders.MAC_HASH]: this.macToken,
      [MacHeaders.MAC_ARGS]: this.macArgs(),
    };
  }

  updateQueryparameters(): void {
    this.queryParameters = new URLSearchParams(`?${window.location.href.split('?')[1]}`);
  }

  getScanQueryParameters(): ScanQueryParams {
    this.updateQueryparameters();
    return {
      validations: this.getInvalidParameters(),
      value: {
        name: this.queryParameter(ScanQueryParametersEnum.NAME),
        street: this.queryParameter(ScanQueryParametersEnum.STREET),
        zip: this.queryParameter(ScanQueryParametersEnum.ZIP),
        country: this.queryParameter(ScanQueryParametersEnum.COUNTRY),
      },
    };
  }

  hasQueryParameters(): boolean {
    const queryparams: string[] = [];
    this.queryParameters.forEach(value => {
      if (value) {
        queryparams.push(value);
      }
    });
    return !!queryparams.length;
  }

  getJobIdQueryParameter(): string {
    return this.queryParameter(GenericScanParameters.JOB_ID);
  }

  getChannelsParameter(): string {
    return this.queryParameter(GenericScanParameters.CHANNELS);
  }

  getFormEnabledQueryParameter(): string {
    return this.queryParameter(GenericScanParameters.FORM_ENABLED);
  }

  getEstablishmentIdQueryParameter(): string {
    return this.queryParameter(GenericScanParameters.ESTABLISHMENT_ID);
  }

  private getInvalidParameters(): string[] {
    const queryParamsValidation: string[] = [];
    Object.keys(ScanQueryParametersEnum).forEach((enumParamater: string) => {
      if (!this.queryParameter(ScanQueryParametersEnum[enumParamater])) {
        queryParamsValidation.push(enumParamater);
      }
    });
    return queryParamsValidation;
  }

  private macArgs(): string {
    return [this.customerUID, this.establishmentID, this.timestamp].join(this.SEPARATOR);
  }

  isOpenRoute(routeToVerify?: string): boolean {
    const navigationURL = routeToVerify ? routeToVerify : this._navigationRoute;
    const openRoutes = [...(this.config.get('whiteList') as [])];
    return openRoutes.filter(route => navigationURL.indexOf(route) !== -1).length >= 1;
  }
}
